import React, { useState } from 'react';
import ExpandedRowContent from './ExpandedRowContent';
import ZillowEstimateCell from './ZillowEstimateCell';
import { getProfitColor } from '../utils/calculators';
import { handleAddressClick } from '../utils/addressHandlers';
import { formatAddressForDisplay, formatCityState, addressStyles } from '../utils/addressFormatters';
import { useRole } from '../../../contexts/RoleContext';
import SubscriptionModal from '../../modals/SubscriptionModal';
import { Button, Box, Tooltip } from '@mui/material';
import { Lock, Star } from '@mui/icons-material';

const ListingTableRow = ({
  listing,
  propertyData,
  isExpanded,
  onToggleExpansion,
  onAddressClick,
  onZillowClick,
  onSummaryClick,
  onPropertyDetailsClick,
  onResidentDetailsClick
}) => {
  const listingId = listing.id || listing.property_id;
  const { isPro } = useRole();
  const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false);
  const [modalFeature, setModalFeature] = useState('');
  
  const isProUser = isPro();

  const handleRowClick = () => {
    onToggleExpansion(listingId);
  };

  const handleAddressLinkClick = (e) => {
    e.stopPropagation(); // Prevent row expansion
    onAddressClick(e, listing.address, listing.city, listing.state);
  };

  const handleRestrictedColumnClick = (e, feature) => {
    e.stopPropagation();
    if (!isProUser) {
      setModalFeature(feature);
      setSubscriptionModalOpen(true);
    }
  };

  const handlePropertyDetailsClick = (e) => {
    e.stopPropagation();
    if (!isProUser) {
      setModalFeature('Property Details');
      setSubscriptionModalOpen(true);
    } else {
      onPropertyDetailsClick(e, listing);
    }
  };

  // Style for blurred content
  const blurredStyle = {
    filter: 'blur(3px)',
    pointerEvents: 'none',
    userSelect: 'none'
  };

  const restrictedCellStyle = {
    position: 'relative',
    cursor: isProUser ? 'default' : 'pointer'
  };

  const RestrictedOverlay = ({ feature }) => (
    <Box
      sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 0.5
      }}
    >
      <Tooltip title={`Upgrade to Pro to view ${feature}`}>
        <Button
          size="small"
          variant="contained"
          startIcon={<Lock />}
          sx={{
            minWidth: 'auto',
            px: 1,
            py: 0.5,
            fontSize: '0.75rem',
            bgcolor: 'warning.main',
            '&:hover': {
              bgcolor: 'warning.dark'
            }
          }}
          onClick={(e) => handleRestrictedColumnClick(e, feature)}
        >
          Pro
        </Button>
      </Tooltip>
    </Box>
  );

  return (
    <React.Fragment>
      <tr 
        onClick={handleRowClick}
        className={`${isExpanded ? 'bg-[#f0fff3]' : 'hover:bg-gray-50'} cursor-pointer transition-colors duration-150 ease-in-out`}
      >
        <td className="col-sale-date px-4 py-2 whitespace-nowrap text-sm text-black font-medium">
          <div className="flex flex-col">
            <span className={listing.isAdjourned ? 'text-orange-500' : ''}>
              {listing.sale_date || 'N/A'}
            </span>
            {listing.isAdjourned && <span className="text-xs text-orange-500">Adjourned</span>}
          </div>
        </td>
        
        <td className="col-property px-4 py-2 whitespace-normal">
          <div className={`max-w-full ${addressStyles.container}`}>
            <div className={`${addressStyles.addressHover} truncate`}>
              <a 
                href="#" 
                onClick={handleAddressLinkClick} 
                className="hover:underline"
              >
                {formatAddressForDisplay(listing.address)}
              </a>
            </div>
            <div className={`${addressStyles.cityState} truncate`}>
              {formatCityState(listing.city, listing.state)}
            </div>
          </div>
        </td>
        
        {/* Upset Value Column - Restricted for non-pro users */}
        <td 
          className="col-upset px-4 py-2 whitespace-nowrap text-sm text-black font-medium"
          style={restrictedCellStyle}
          onClick={(e) => !isProUser && handleRestrictedColumnClick(e, 'Upset Value')}
        >
          <div style={!isProUser ? blurredStyle : {}}>
            {listing.upset_value || 'N/A'}
          </div>
          {!isProUser && <RestrictedOverlay feature="Upset Value" />}
        </td>
        
        <td className="col-downpayment px-4 py-2 whitespace-nowrap text-sm text-black font-medium">
          {listing.downpayment || 'N/A'}
        </td>
        
        <td className="col-zillow px-4 py-2 whitespace-nowrap text-sm">
          <ZillowEstimateCell 
            listing={listing} 
            propertyData={propertyData} 
            listingId={listingId}
            isProUser={isProUser}
            onSubscriptionModal={() => {
              setModalFeature('Zillow Estimate');
              setSubscriptionModalOpen(true);
            }}
          />
        </td>
        
        {/* Profit Column - Restricted for non-pro users */}
        <td 
          className="col-profit px-4 py-2 whitespace-nowrap"
          style={restrictedCellStyle}
          onClick={(e) => !isProUser && handleRestrictedColumnClick(e, 'Profit Calculation')}
        >
          {propertyData?.status === 'success' && listing.profit !== undefined ? (
            <div style={!isProUser ? blurredStyle : {}}>
              <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getProfitColor(listing.profit)}`}>
                {listing.profitFormatted}
              </span>
            </div>
          ) : (
            <div style={!isProUser ? blurredStyle : {}}>
              <span className="text-black font-medium">N/A</span>
            </div>
          )}
          {!isProUser && <RestrictedOverlay feature="Profit Calculation" />}
        </td>
      </tr>
      
      {isExpanded && (
        <tr className="bg-[#f0fff3] border-t border-[#d7f5df]">
          <td colSpan={6} className="px-6 py-4">
            <ExpandedRowContent
              listing={listing}
              propertyData={propertyData}
              onAddressClick={onAddressClick}
              onZillowClick={onZillowClick}
              onSummaryClick={onSummaryClick}
              onPropertyDetailsClick={handlePropertyDetailsClick}
              onResidentDetailsClick={onResidentDetailsClick}
              isProUser={isProUser}
              onSubscriptionModal={() => {
                setModalFeature('Property Details');
                setSubscriptionModalOpen(true);
              }}
            />
          </td>
        </tr>
      )}

      {/* Subscription Modal */}
      <SubscriptionModal
        open={subscriptionModalOpen}
        onClose={() => setSubscriptionModalOpen(false)}
        feature={modalFeature}
      />
    </React.Fragment>
  );
};

export default ListingTableRow;